import React from 'react';
import { Play, Info, Star, Layers } from 'lucide-react';
import { MediaItem, WatchHistoryItem } from '../types';

interface MediaCardProps {
  item: MediaItem;
  onSelect: (item: MediaItem) => void;
  onPlay: (item: MediaItem, episodeIndex?: number, initialTime?: number) => void;
  onToggleWatchlist?: (item: MediaItem) => void;
  isInWatchlist?: boolean;
  historyItem?: WatchHistoryItem;
  directPlayOnSelect?: boolean;
  className?: string;
}

const MediaCardComponent: React.FC<MediaCardProps> = ({
  item,
  onSelect,
  onPlay,
  historyItem,
  directPlayOnSelect = false,
  className
}) => {
  const getResolvedEpisodeIndex = () => {
    if (item.type !== 'series' || !item.episodes || item.episodes.length === 0) return 0;
    if (historyItem?.episode) {
      const foundIdx = item.episodes.findIndex(
        (ep, idx) => (historyItem.season ? (ep.season || 1) === historyItem.season : true) && 
                     (ep.episode === historyItem.episode || (idx + 1) === historyItem.episode)
      );
      if (foundIdx >= 0) return foundIdx;
    }
    return 0;
  };

  const handleCardClick = () => {
    if (directPlayOnSelect) {
      const epIdx = getResolvedEpisodeIndex();
      const resumeTime = historyItem?.progressSeconds || 0;
      onPlay(item, epIdx, resumeTime);
    } else {
      onSelect(item);
    }
  };

  return (
    <div 
      id={`media-card-${item.id}`}
      data-focusable="true"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className={`group relative tv-card cursor-pointer select-none rounded-xl outline-none transition-all duration-200 transform group-hover:-translate-y-1 focus:-translate-y-1 ${className || 'flex-shrink-0 w-32 sm:w-36 md:w-[calc((100vw-5.5rem)/5.2)] lg:w-[calc((min(100vw,80rem)-6.5rem)/5.25)] max-w-[200px]'}`}
    >
      {/* Poster Container */}
      <div 
        className="relative aspect-[2/3] w-full rounded-xl overflow-hidden bg-[#0c1220] border border-white/10 group-hover:border-sky-400 group-focus:border-sky-400 shadow-sm group-hover:shadow-lg group-hover:shadow-sky-500/20 transition-all duration-200"
      >
        <img
          src={item.poster}
          alt={item.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 group-focus:scale-105"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />

        {/* Top Badges */}
        <div className="absolute top-1.5 left-1.5 right-1.5 flex items-center justify-between pointer-events-none z-10">
          <div className="flex items-center gap-1">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-black tracking-wider uppercase bg-sky-500 text-black shadow-sm">
              {item.type === 'series' ? 'SÈRIE' : 'FILM'}
            </span>
            {directPlayOnSelect && historyItem && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase bg-blue-600 text-white shadow-sm flex items-center gap-0.5">
                <Play className="w-2 h-2 fill-white" />
                <span>Reprèn</span>
              </span>
            )}
          </div>
          {item.rating && (
            <div className="flex items-center gap-0.5 bg-black/80 backdrop-blur-md px-1.5 py-0.5 rounded text-[10px] font-bold text-sky-300 border border-white/10">
              <Star className="w-2.5 h-2.5 fill-sky-400 text-sky-400" />
              <span>{item.rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Bottom Shadow Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#070b14] via-black/25 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />

        {/* Quick Action Overlay on Hover or TV focus */}
        <div className="absolute inset-0 flex flex-col justify-end p-2 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-150 z-20">
          <div className="flex items-center justify-center gap-2 mb-1.5">
            <button
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                const epIdx = getResolvedEpisodeIndex();
                const resumeTime = historyItem?.progressSeconds || 0;
                onPlay(item, epIdx, resumeTime);
              }}
              className="p-2 sm:p-2.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 text-white font-bold shadow-md shadow-sky-500/40 hover:scale-110 active:scale-95 transition-transform cursor-pointer"
              title={directPlayOnSelect ? "Reprèn la reproducció" : "Reprodueix"}
            >
              <Play className="w-4 h-4 fill-white" />
            </button>
            <button
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item);
              }}
              className="p-2 sm:p-2.5 rounded-full bg-black/70 hover:bg-black text-white hover:text-sky-300 border border-white/20 shadow-md hover:scale-110 active:scale-95 transition-transform cursor-pointer"
              title="Detalls de la sèrie o pel·lícula"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>

          {/* Episode Progress Bar in hover overlay */}
          {historyItem && historyItem.progressSeconds > 0 && (
            <div className="w-full bg-white/20 h-1.5 rounded-full overflow-hidden mb-1">
              <div 
                className="bg-sky-400 h-full rounded-full" 
                style={{ 
                  width: `${Math.min(100, Math.max(5, (historyItem.progressSeconds / (historyItem.durationSeconds || 1400)) * 100))}%` 
                }} 
              />
            </div>
          )}
        </div>

        {/* Persistent bottom progress bar for in-progress items */}
        {historyItem && historyItem.progressSeconds > 5 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40 z-15 pointer-events-none">
            <div 
              className="bg-sky-400 h-full transition-all duration-300"
              style={{ 
                width: `${Math.min(100, Math.max(4, (historyItem.progressSeconds / (historyItem.durationSeconds || 1400)) * 100))}%` 
              }} 
            />
          </div>
        )}
      </div>

      {/* Title & Metadata */}
      <div className="mt-2 space-y-0.5">
        <h3 className="text-xs sm:text-sm font-bold text-white line-clamp-1 group-hover:text-sky-400 transition-colors">
          {item.title}
        </h3>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
          <span>{item.year || '2024'}</span>
          <span className="w-0.5 h-0.5 rounded-full bg-slate-600" />
          <span>
            {item.type === 'series' 
              ? `${item.numberOfEpisodes || item.episodes?.length || 1} ep` 
              : `${item.runtime || 90}m`}
          </span>
        </div>
      </div>
    </div>
  );
};

export const MediaCard = React.memo(MediaCardComponent);
